import { TRANSFERZ_EXPERIENCE_ID } from 'constants/experience'
import { excludeNullOrUndefined } from 'checkout/utils'
import { knownPriceBreakdownTitle } from 'checkout/constants/priceBreakdownTitles'
import { sum } from 'lib/array/arrayUtils'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { createSelector } from 'reselect'
import getLoyaltyTransferItemView from '../itemViews/getLoyaltyTransferItemView'
import getTransferItemsView from '../itemViews/getTransferItemsView'
import { getTransferMobileAppDiscountTotal } from '../experience'

function compactTransferLocation(location?: string): string {
  return location?.split(',')[0]?.replace(/\s*\([A-Z]{3}\)/, '').trim() ?? ''
}

export function buildTransferAdditionalInfo(transfer: App.ExperienceTransferView, isTransferz = false): Array<{ text: string, value?: number }> {
  const intl = getCurrentIntl()
  if (isTransferz) {
    const isReturn = transfer.type === 'HOTEL-TO-AIRPORT'
    const pickup = compactTransferLocation(transfer.pickupPoint?.name ?? (isReturn ? transfer.hotel?.name : transfer.airport?.name))
    const dropoff = compactTransferLocation(transfer.dropoffPoint?.name ?? (isReturn ? transfer.airport?.name : transfer.hotel?.name))
    const journeyScope = isReturn
      ? intl.formatMessage({
          id: 'obTBy0_gDYwIlA6_bYO2',
          defaultMessage: 'Return',
          description: 'Journey scope shown beside a selected Transferz checkout summary extra for the return transfer',
        })
      : intl.formatMessage({
          id: 'b20buftQA6-NFlEjb3wo',
          defaultMessage: 'Pickup',
          description: 'Journey scope shown beside a selected Transferz checkout summary extra for the pickup transfer',
        })

    return [
      ...(pickup && dropoff ? [{
        text: intl.formatMessage({
          id: '7TbCU-KU1ik1NPKhbU_3',
          defaultMessage: '{pickup} → {dropoff}',
          description: 'Compact one-line route summary for an individual Transferz journey in the checkout price summary',
        }, { pickup, dropoff }),
      }] : []),
      ...(transfer.extras?.map(extra => ({
        text: intl.formatMessage({
          id: 'IP130ugXl1svznFzC9F5',
          defaultMessage: '{title} · {journey}',
          description: 'Selected Transferz extra and the journey it applies to in the checkout price summary',
        }, { title: extra.title, journey: journeyScope }),
        value: extra.price * extra.quantity,
      })) ?? []),
    ]
  }

  if (transfer.isHomePickup) {
    const hotelName = transfer.hotel?.name ?? intl.formatMessage({
      id: 'S0WYlbv_5dq6CH8k1kDC',
      defaultMessage: 'Home',
      description: 'Fallback name for home pickup location in transfer',
    })
    const airportName = transfer.airport?.name ?? intl.formatMessage({
      id: 'u8dfximDACStrIbXWRRq',
      defaultMessage: 'Airport',
      description: 'Fallback name for airport location in transfer in the checkout flow',
    })
    return [{
      text: intl.formatMessage(
        {
          id: '8ybNhZqVnZa4Yo98rpKf',
          defaultMessage: '{origin} to {destination}',
          description: 'Transfer route showing origin to destination for transfers',
        },
        { origin: hotelName, destination: airportName },
      ),
    }]
  }

  const optionName = transfer.option?.name ?? ''
  // The number of people actually travelling, not the vehicle's capacity -- a party of
  // 2 in a 3-seat sedan must read "2 passengers". Falls back to the option's capacity
  // for items without traveller data.
  const passengerCount = (transfer.travellers?.adults ?? 0) + (transfer.travellers?.children ?? 0)
  const maxPassengers = passengerCount > 0 ? passengerCount : (transfer.option?.maxPassengers ?? 0)
  const maxCheckedBags = transfer.option?.maxCheckedBags ?? 0

  // bookingOptions fields are optional on the svc-experiences offer config, and the
  // mapper coerces absent values to 0 -- a capacity of 0 is never real, so omit the
  // line rather than rendering "0 passengers" / "Maximum 0 bags".
  return [
    { text: optionName },
    ...(maxPassengers > 0 ? [{
      text: intl.formatMessage({
        id: 'O7ef5SoQ3cWRY7udKaa5',
        defaultMessage: '{count, plural, one {# passenger} other {# passengers}}',
        description: 'Passenger count for transfer option in the airport transfer booking section',
      }, { count: maxPassengers }),
    }] : []),
    ...(maxCheckedBags > 0 ? [{
      text: intl.formatMessage({
        id: 'c7oMXHxTrnEu7l143TAK',
        defaultMessage: 'Maximum {maxCheckedBags, plural, one {# bag} other {# bags}}',
        description: 'Maximum bag allowance for transfer in the airport transfer booking section',
      }, { maxCheckedBags }),
    }] : []),
  ]
}

function homeLoyaltyTransferBreakdown(views: Array<App.Checkout.LoyaltyTransferItemView>): App.Checkout.PriceBreakdownView | undefined {
  if (views.length === 0) return

  const intl = getCurrentIntl()
  return {
    title: knownPriceBreakdownTitle('transfers'),
    price: 0,
    memberPrice: 0,
    alwaysShowItemPrice: true,
    additionalInfoText: views.map(view => intl.formatMessage(
      {
        id: '8ybNhZqVnZa4Yo98rpKf',
        defaultMessage: '{origin} to {destination}',
        description: 'Transfer route showing origin to destination for transfers',
      },
      {
        origin: view.pickUpPlace.address,
        destination: view.dropOffAirport.name,
      },
    )),
    items: [],
  }
}

const getTransfersBreakdownView = createSelector(
  (state: App.State) => getTransferItemsView(state),
  (state: App.State) => getTransferMobileAppDiscountTotal(state),
  (state: App.State) => getLoyaltyTransferItemView(state),
  (viewWithStatus, transferMobileAppDiscountTotals, loyaltyTransferItemView): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    if (viewWithStatus.data.length === 0) {
      const homeTransfer = homeLoyaltyTransferBreakdown(loyaltyTransferItemView.data)
      return {
        hasRequiredData: viewWithStatus.hasRequiredData,
        data: homeTransfer ? [homeTransfer] : [],
      }
    }

    const nonBundledTransferViews = viewWithStatus.data.filter(itemView => !itemView.item.isAccomBundle)
    const price = sum(nonBundledTransferViews, itemView => itemView.totals.price)
    const totalMemberPrice = sum(nonBundledTransferViews, itemView => itemView.totals.memberPrice || itemView.totals.price)
    const memberPrice = totalMemberPrice < price ? totalMemberPrice : 0
    const transferBreakdownView: App.Checkout.PriceBreakdownView = {
      title: knownPriceBreakdownTitle('transfers'),
      price: price + transferMobileAppDiscountTotals.amount,
      memberPrice,
      additionalInfoText: [],
      mobileAppDiscount: transferMobileAppDiscountTotals,
      items: viewWithStatus.data.filter(excludeNullOrUndefined).map<App.Checkout.TransferItemBreakdownView>(itemView => {
        const isBundled = !!itemView.item.isAccomBundle
        return {
          itemId: itemView.item.itemId,
          title: itemView.item.transfer.title ?? '',
          price: isBundled ? 0 : itemView.totals.price + (itemView.totals.mobileAppDiscount?.amount ?? 0),
          hidePrice: isBundled,
          memberPrice: isBundled ? 0 : itemView.totals.memberPrice,
          itemType: itemView.item.itemType,
          offerId: itemView.item.transfer.option?.id ?? '',
          hasLoyaltyBenefit: itemView.item.hasLoyaltyBenefit,
          additionalInfo: buildTransferAdditionalInfo(itemView.item.transfer, itemView.item.experienceId === TRANSFERZ_EXPERIENCE_ID),
          additionalElements: [],
        }
      }),
    }

    return {
      hasRequiredData: true,
      data: [transferBreakdownView],
    }
  },
)

export default getTransfersBreakdownView
